import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import lodash from 'lodash';
import { Suspense, useMemo } from 'react';

const { maxBy } = lodash;

import CategoryDistributionChart from '@/components/Charts/CategoryDistributionChart';
import Footer from '@/components/Footer';
import RecentTrends from '@/components/RecentTrends';

import { fetchCategoryDistributionByHour, fetchRecentTrends } from '@/lib/serverFns';

import { env } from '@/env';

export const Route = createFileRoute('/')({
  component: Home,
  loader: async ({ context }) => {
    const recentTrends = await context.queryClient.ensureQueryData({
      queryKey: ['recentTrends'],
      queryFn: fetchRecentTrends,
      staleTime: env.BUNDLED_DATA_STALE_TIME_SECONDS,
    });

    const trendsByHourlyCategories = await context.queryClient.ensureQueryData({
      queryKey: ['trendsByHourlyCategories'],
      queryFn: fetchCategoryDistributionByHour,
      staleTime: env.BUNDLED_DATA_STALE_TIME_SECONDS,
    });

    return {
      recentTrends,
      trendsByHourlyCategories,
      generatedAt: new Date().getTime(),
    };
  },
  headers: (ctx) => ({
    // 'cache-control': `public, max-age=${env.CACHE_TTL_SECONDS}, stale-while-revalidate=${env.CACHE_TTL_SECONDS}`,
    'Cache-Control': `public, max-age=0, must-revalidate, s-maxage=${env.VITE_CACHE_TTL_SECONDS}, stale-while-revalidate=${env.MAX_STALE_WHILE_REVALIDATE_SECONDS}`,
  }),
  head: () => ({
    meta: [
      {
        name: 'description',
        content: "What's trending on Bluesky right now?",
      },
      {
        title: 'Bluesky Trends',
      },
    ],
  }),
});

function Home() {
  const { data: recentTrends = [] } = useSuspenseQuery({
    queryKey: ['recentTrends'],
    queryFn: fetchRecentTrends,
  });

  const { data: trendsByHourlyCategories = [] } = useSuspenseQuery({
    queryKey: ['trendsByHourlyCategories'],
    queryFn: fetchCategoryDistributionByHour,
  });

  const lastUpdatedAt = useMemo(
    () =>
      recentTrends.length > 0
        ? (maxBy(recentTrends, (t) => t.lastUpdatedAt)?.lastUpdatedAt?.toString() ?? '')
        : '',
    [recentTrends]
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-0 sm:p-8">
      <div className="flex w-full max-w-7xl flex-col gap-4 rounded-lg border bg-gray-100 px-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:gap-8">
          <Suspense fallback={<div>Loading...</div>}>
            <RecentTrends recentTrends={recentTrends} />
            <CategoryDistributionChart data={trendsByHourlyCategories} />
          </Suspense>
        </div>

        <Footer lastUpdatedAt={lastUpdatedAt} />
      </div>
    </div>
  );
}
